import {
  onBorderSettingsChange,
  onDisableDraggingChange,
  onFilterInfluencesChange,
  onGlobalChange,
  onModelIdChange,
  onNameSettingsChange,
  onPositionChange,
  onPositionConfigChange,
  onRealDataChange,
  onShadowSettingsChange,
  onShowBackgroundChange,
  onSqlDataChange,
} from 'modules/settingsContainer/common/data/DefaultFilterDataSettings/constants';
import { RealDataSettings } from 'modules/settingsContainer/common/data/RealDataSettings';
import { FilterInfluenceSettings } from 'modules/settingsContainer/common/FilterInfluenceSettings';
import { ModelSelectorSettings } from 'modules/settingsContainer/common/ModelSelectorSettings';
import { ContainerSettings } from 'modules/settingsContainer/common/view/ContainerSettings';
import { PositionSettings } from 'modules/settingsContainer/common/view/PositionSettings';
import { NameFilterSettings } from 'modules/settingsContainer/NameFilterSettings';
import { useDataSettingsMetric } from 'modules/visualisations/hooks/dataSettingsMetric';
import React from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store';
import { toggleInfluenceFiltersMode } from 'store/reducers/board';
import { getInfluenceFiltersMode } from 'store/reducers/board/getters';
import { getFilters } from 'store/reducers/filters/getters';
import { FilterInterface, FilterType } from 'store/reducers/filters/types';
import { getVisualisations } from 'store/reducers/visualisations/getters';
import { ReactChildrenType } from 'types/global';
import { SqlDataRequestInterface } from 'types/store';
import { getAstFromSql } from 'utils/SQL/genereteAst';
import { applyToAllInfluences } from 'utils/utils';
import { SqlFilterSettings } from '../SqlFilterSettings';
import { useGetActiveFilter } from 'modules/filters/hooks/getActiveFilter';

interface DefaultFilterDataSettingsProps<Type extends FilterType> {
  data: FilterInterface<Type>;
  children?: ReactChildrenType;
  modelIdValue: string;
}

export const DefaultFilterDataSettings = <Type extends FilterType>({
  children,
  data,
  modelIdValue,
}: DefaultFilterDataSettingsProps<Type>) => {
  const dispatch = useAppDispatch();
  const influenceFiltersMode = useSelector(getInfluenceFiltersMode);
  const visualisations = useSelector(getVisualisations);
  const filters = useSelector(getFilters);
  const {
    data: { modelId },
  } = useGetActiveFilter({ type: 'single' });
  const { modelMetaData } = useDataSettingsMetric(modelId);

  const {
    id,
    isRealData,
    isGlobal,
    position,
    disableDragging,
    shadowSettings,
    borderSettings,
    showBackground,
    positionConfig,
    nameSettings,
    filterInfluences,
    sqlData,
  } = data;

  const toggleInfluenceFilterMode = () => {
    dispatch(toggleInfluenceFiltersMode());
  };

  const onSaveSql = ({ incisionRequest, filterAndGroupRequest }: SqlDataRequestInterface) => {
    const isValidIncisionAst = !incisionRequest ? true : getAstFromSql(`SELECT ${incisionRequest}`);
    const isValidFilterAst = getAstFromSql(`SELECT * ${filterAndGroupRequest}`);

    if (isValidIncisionAst && isValidFilterAst) {
      onSqlDataChange({
        sqlData: {
          incisionRequest,
          filterAndGroupRequest,
        },
      });
    }
  };

  const onApplyToAllFilterInfluences = (payload: boolean) => () => {
    if (!influenceFiltersMode) {
      toggleInfluenceFilterMode();
    }

    const influenceData = applyToAllInfluences({ ...visualisations, ...filters }, payload, id);

    onFilterInfluencesChange({ ...filterInfluences, ...influenceData });
  };

  return (
    <>
      <ContainerSettings
        positionConfig={positionConfig}
        disableDragging={disableDragging}
        showBackground={showBackground}
        borderSettings={borderSettings}
        shadowSettings={shadowSettings}
        onExternalPositionChange={onPositionConfigChange}
        onExternalDisableDraggingChange={onDisableDraggingChange}
        onExternalBorderSettingsChange={onBorderSettingsChange}
        onExternalShowBackgroundChange={onShowBackgroundChange}
        onExternalShadowSettingsChange={onShadowSettingsChange}
      />
      <RealDataSettings value={isRealData} onChange={onRealDataChange} />
      <ModelSelectorSettings value={modelIdValue} onChange={onModelIdChange} />
      <NameFilterSettings isRealData={isRealData} value={nameSettings} onChange={onNameSettingsChange} />
      {children}
      <SqlFilterSettings sqlData={sqlData} onSave={onSaveSql} modelMetaData={modelMetaData} />
      <PositionSettings value={position} onChange={onPositionChange} />
      <FilterInfluenceSettings
        value={isGlobal}
        onChange={onGlobalChange}
        influenceModeValue={influenceFiltersMode}
        onToggleInfluenceMode={toggleInfluenceFilterMode}
        onApplyToAllFilterInfluences={onApplyToAllFilterInfluences}
      />
    </>
  );
};
